import { useRef } from "react";

import { logger } from "@/lib/logger";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type {
  CustomFilterHook,
  CustomFilterHookParams,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";

/**
 * Call every `customFilterHooks` entry a config declares, return only the one
 * the active view asked for.
 *
 * WHY THE REGISTRY IS FROZEN ON FIRST RENDER. React requires a component to
 * call the SAME hooks in the SAME order on every render. This function calls one
 * hook per key in `config.customFilterHooks` — so if that map ever changed shape
 * between renders, the entity page would die with "Rendered more hooks than
 * during the previous render", a white screen with no recovery. The previous
 * version defended against that with a comment ("the config MUST be stable")
 * and nothing else, plus an early `return null` for the empty case that made the
 * count vary between 0 and N as well.
 *
 * Today every entity config is a module-level constant, so the map is stable and
 * the crash is unreachable. It becomes reachable the moment someone builds a
 * config inside a component or registers a view dynamically — and the symptom
 * then is not "my new filter didn't show up", it is the whole page going blank.
 *
 * So the key set is captured on the first render of this component instance and
 * reused for the life of it. The hook count is fixed by construction, the crash
 * cannot happen, and a config that changes shape degrades to "the new hook is
 * ignored until this page remounts" — with a warning naming the keys, because a
 * silent no-op is its own kind of bug. Remounting the entity page (any route
 * change) picks up the new registry.
 *
 * The alternative fix — one child component per hook — is the textbook answer to
 * a variable hook count, but the result is consumed during THIS render, so it
 * would mean restructuring `useDataFetching` around a callback. That is a
 * deliberate design change, not a prerequisite for closing the crash.
 */
export function useActiveCustomFilter<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
  activeViewId: string | null,
  queryParams: { first: number; after: string | null }
) {
  const customFilterHooks = config.customFilterHooks || {};

  // The FUNCTIONS are frozen, not just their keys: freezing keys alone would
  // still leave a slot with no hook to call if the config later dropped one,
  // and skipping that call shifts every later slot — the same crash.
  const registry = useRef<ReadonlyArray<readonly [string, CustomFilterHook<T>]> | null>(null);
  if (registry.current === null) {
    registry.current = Object.keys(customFilterHooks)
      .sort()
      .flatMap((key) => {
        const hook = customFilterHooks[key];
        return typeof hook === "function" ? [[key, hook] as const] : [];
      });
  }
  const frozen = registry.current;

  const declaredNow = Object.keys(customFilterHooks).sort();
  const mountedWith = frozen.map(([key]) => key);
  if (
    declaredNow.length !== mountedWith.length ||
    declaredNow.some((key, i) => key !== mountedWith[i])
  ) {
    warnRegistryDrift(mountedWith, declaredNow);
  }

  const results: Record<string, ReturnType<CustomFilterHook<T>>> = {};

  // An indexed loop, not `forEach`: the hook calls must sit at the top level of
  // this function body, never inside a callback.
  for (let i = 0; i < frozen.length; i++) {
    const [key, hook] = frozen[i];
    results[key] = hook({
      first: queryParams.first,
      after: queryParams.after,
      enabled: activeViewId === key,
    });
  }

  return activeViewId && results[activeViewId] ? results[activeViewId] : null;
}

/** Reported once per drift, and only outside production — this is a wiring bug. */
function warnRegistryDrift(mountedWith: string[], declaredNow: string[]): void {
  if (process.env.NODE_ENV === "production") return;
  logger.warn("customFilterHooks changed shape after mount; the change is ignored", {
    op: "universalEntityPage.activeCustomFilter",
    mountedWith,
    declaredNow,
  });
}
